import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import ScheduleModal from "../components/ScheduleModal";
import styles from "./AdminTelegram.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { isQBankCode, QBankCode } from "../utils/qbankProfile";

interface TelegramContent {
    id: string;
    question_id: string | null;
    content_type: "text" | "photo" | "poll" | "media_group";
    status: "draft" | "generating" | "ready" | "posted";
    content_payload: Record<string, any>;
    scheduled_for: string | null;
    scheduling_status: "unscheduled" | "scheduled" | "posting" | "completed" | "failed";
    scheduling_error: string | null;
    telegram_message_id: string | null;
    posted_at: string | null;
    publish_attempts: number | null;
    last_publish_error: string | null;
    generation_error: string | null;
    created_at: string;
    updated_at: string;
    qbank_questions?: {
        id?: string;
        question_text: string;
        specialty: string;
        correct_answer?: string;
        options?: { label: string; text: string }[];
    };
}

interface EligibleCollection {
    id: string;
    title: string;
    year: number;
    month: number;
    questions_count: number;
    eligible_count: number;
}

interface EligibleQuestion {
    id: string;
    question_text: string;
    specialty: string;
    correct_answer: string;
    options: { label: string; text: string }[];
    difficulty?: string;
}

interface BotInfo {
    id: string;
    bot_username: string;
    bot_id: string;
    channel_id: string;
    channel_title: string | null;
    is_active: boolean;
    last_post_at: string | null;
    connection_verified_at: string | null;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    draft: { label: "Draft", color: "#937663" },
    generating: { label: "Generating...", color: "#faad33" },
    ready: { label: "Ready", color: "#39a3a1" },
    posted: { label: "Posted", color: "#1fc16b" },
};

const SCHEDULING_STATUS_LABELS: Record<string, { label: string; color: string }> = {
    unscheduled: { label: "Not Scheduled", color: "#937663" },
    scheduled: { label: "Scheduled", color: "#2563eb" },
    posting: { label: "Posting...", color: "#faad33" },
    completed: { label: "Posted", color: "#1fc16b" },
    failed: { label: "Failed", color: "#dc2626" },
};

const CONTENT_TYPE_ICONS: Record<string, string> = {
    text: "Aa",
    photo: "Img",
    poll: "Quiz",
    media_group: "Album",
};

const AdminTelegram: React.FC = () => {
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // --- Tab state ---
    const [mainTab, setMainTab] = useState<"posts" | "compose" | "qbank" | "setup">("posts");
    const initialBank = searchParams.get("bank") || undefined;
    const [activeBank, setActiveBank] = useState<QBankCode>(isQBankCode(initialBank) ? initialBank : "sdle");

    // --- Posts tab state ---
    const [contents, setContents] = useState<TelegramContent[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState<"all" | "ready" | "scheduled" | "posted">("all");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Modals
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [selectedContent, setSelectedContent] = useState<TelegramContent | null>(null);
    const [schedulingContentId, setSchedulingContentId] = useState<string | null>(null);
    const [schedulingInitialDate, setSchedulingInitialDate] = useState<string | null>(null);

    // Publishing
    const [publishing, setPublishing] = useState(false);

    // Toast / inline feedback
    const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

    // Editing in preview modal
    const [editingMessage, setEditingMessage] = useState(false);
    const [editMessageDraft, setEditMessageDraft] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);

    // --- Compose tab state ---
    const [composeType, setComposeType] = useState<"text" | "photo">("text");
    const [composeMessage, setComposeMessage] = useState("");
    const [composeImageUrl, setComposeImageUrl] = useState("");
    const [composeParseMode, setComposeParseMode] = useState<"HTML" | "Markdown">("HTML");
    const [composeSaving, setComposeSaving] = useState(false);

    // --- QBank tab state ---
    const [pickerCollections, setPickerCollections] = useState<EligibleCollection[]>([]);
    const [pickerQuestions, setPickerQuestions] = useState<EligibleQuestion[]>([]);
    const [pickerTotalEligible, setPickerTotalEligible] = useState(0);
    const [loadingCollections, setLoadingCollections] = useState(false);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState<string>("");
    const [generating, setGenerating] = useState(false);
    const [pickerView, setPickerView] = useState<"collections" | "questions">("collections");
    const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
    const [pickerSearch, setPickerSearch] = useState("");
    const [pickerSpecialtyFilter, setPickerSpecialtyFilter] = useState("all");

    // --- Setup tab state ---
    const [botInfo, setBotInfo] = useState<BotInfo | null>(null);
    const [loadingBot, setLoadingBot] = useState(true);
    const [setupToken, setSetupToken] = useState("");
    const [setupChannelId, setSetupChannelId] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState<Record<string, any> | null>(null);
    const [savingBot, setSavingBot] = useState(false);
    const [testingSend, setTestingSend] = useState(false);

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    const showToast = (message: string, type: "success" | "error") => {
        setToast({ message, type });
    };

    useEffect(() => {
        if (!toast) return;
        const timer = setTimeout(() => setToast(null), 4000);
        return () => clearTimeout(timer);
    }, [toast]);

    // =============================================
    // POSTS TAB
    // =============================================

    const loadContents = useCallback(async () => {
        if (!user?.id) return;

        try {
            const params = new URLSearchParams({
                user_id: user.id,
                page: page.toString(),
                bank: activeBank,
            });

            if (activeFilter === "scheduled") {
                params.set("scheduling_status", "scheduled");
            } else if (activeFilter !== "all") {
                params.set("status", activeFilter);
            }

            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram?${params}`);
            if (res.ok) {
                const data = await res.json();
                setContents(data.items || []);
                setTotalPages(data.pages || 1);
            }
        } catch (err) {
            console.error("Failed to load Telegram content:", err);
        } finally {
            setLoading(false);
        }
    }, [apiUrl, user?.id, page, activeFilter, activeBank]);

    useEffect(() => {
        if (mainTab === "posts") loadContents();
    }, [loadContents, mainTab]);

    // Polling for posting status
    useEffect(() => {
        const hasPosting = contents.some((c) => c.scheduling_status === "posting");
        if (hasPosting) {
            const interval = setInterval(loadContents, 3000);
            return () => clearInterval(interval);
        }
    }, [contents, loadContents]);

    const handleOpenPreview = (content: TelegramContent) => {
        setSelectedContent(content);
        setShowPreviewModal(true);
    };

    const handleDelete = async (contentId: string) => {
        if (!user?.id || !window.confirm("Delete this content?")) return;

        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/${contentId}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id }),
            });
            if (res.ok) {
                setShowPreviewModal(false);
                loadContents();
            }
        } catch (err) {
            console.error("Failed to delete:", err);
        }
    };

    const handlePublish = async (contentId: string) => {
        if (!user?.id) return;
        if (!window.confirm("Publish this post to the Telegram channel now?")) return;
        setPublishing(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/${contentId}/publish`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id }),
            });
            const data = await res.json();
            if (data.success) {
                setShowPreviewModal(false);
                showToast("Published successfully!", "success");
                loadContents();
            } else {
                showToast(`Failed to publish: ${data.error || "Unknown error"}`, "error");
            }
        } catch (err) {
            console.error("Failed to publish:", err);
            showToast("Failed to publish: Network error", "error");
        } finally {
            setPublishing(false);
        }
    };

    const handleRetry = async (contentId: string) => {
        if (!user?.id) return;
        setPublishing(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/${contentId}/retry`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id }),
            });
            const data = await res.json();
            if (data.success) {
                setShowPreviewModal(false);
                showToast("Successfully posted to Telegram!", "success");
                loadContents();
            } else {
                showToast(`Failed to retry: ${data.error || "Unknown error"}`, "error");
            }
        } catch (err) {
            console.error("Failed to retry:", err);
            showToast("Failed to retry: Network error", "error");
        } finally {
            setPublishing(false);
        }
    };

    const handleMarkPosted = async (contentId: string) => {
        if (!user?.id) return;
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/${contentId}/mark-posted`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id }),
            });
            if (res.ok) {
                setShowPreviewModal(false);
                loadContents();
            }
        } catch (err) {
            console.error("Failed to mark posted:", err);
        }
    };

    const handleOpenScheduleModal = (contentId: string, initialDate: string | null) => {
        setSchedulingContentId(contentId);
        setSchedulingInitialDate(initialDate);
        setShowScheduleModal(true);
    };

    const handleSchedule = async (datetime: Date) => {
        if (!user?.id || !schedulingContentId) return;
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/${schedulingContentId}/schedule`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    scheduled_for: datetime.toISOString(),
                }),
            });
            if (res.ok) {
                setShowScheduleModal(false);
                setSchedulingContentId(null);
                setShowPreviewModal(false);
                showToast("Scheduled successfully!", "success");
                loadContents();
            } else {
                const error = await res.json();
                showToast(error.error || "Failed to schedule", "error");
            }
        } catch (err) {
            console.error("Failed to schedule:", err);
            showToast("Failed to schedule: Network error", "error");
        }
    };

    const handleUnschedule = async (contentId: string) => {
        if (!user?.id) return;
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/${contentId}/schedule`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id }),
            });
            if (res.ok) {
                setShowPreviewModal(false);
                loadContents();
            }
        } catch (err) {
            console.error("Failed to unschedule:", err);
        }
    };

    const formatScheduledTime = (iso: string): string => {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
        });
    };

    const getContentPreview = (c: TelegramContent): string => {
        const payload = c.content_payload || {};
        if (c.content_type === "poll") {
            return payload.question || "Quiz poll";
        }
        return payload.message || "";
    };

    // =============================================
    // COMPOSE TAB
    // =============================================

    const handleComposeSave = async (publishNow: boolean) => {
        if (!user?.id || !composeMessage.trim()) return;
        if (publishNow && !window.confirm("Publish this post to the Telegram channel now?")) return;
        setComposeSaving(true);
        try {
            // Create post
            const createRes = await authenticatedFetch(`${apiUrl}/api/admin/telegram/compose`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    content_type: composeType,
                    message: composeMessage,
                    image_url: composeType === "photo" ? composeImageUrl : undefined,
                    parse_mode: composeParseMode,
                }),
            });

            if (!createRes.ok) {
                const error = await createRes.json();
                showToast(error.error || "Failed to create post", "error");
                return;
            }

            const created = await createRes.json();

            if (publishNow) {
                const pubRes = await authenticatedFetch(`${apiUrl}/api/admin/telegram/${created.id}/publish`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user.id }),
                });
                const pubData = await pubRes.json();
                if (pubData.success) {
                    showToast("Published successfully!", "success");
                } else {
                    showToast(`Created but failed to publish: ${pubData.error || "Unknown error"}`, "error");
                }
            } else {
                showToast("Draft saved!", "success");
            }

            // Reset compose form
            setComposeMessage("");
            setComposeImageUrl("");
            setMainTab("posts");
            loadContents();
        } catch (err) {
            console.error("Failed to compose:", err);
            showToast("Failed to create post", "error");
        } finally {
            setComposeSaving(false);
        }
    };

    // =============================================
    // QBANK TAB
    // =============================================

    const loadCollections = async () => {
        if (!user?.id) return;
        setLoadingCollections(true);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/telegram/eligible-questions?user_id=${user.id}&bank=${activeBank}`
            );
            if (res.ok) {
                const data = await res.json();
                setPickerCollections(data.collections || []);
                setPickerTotalEligible(data.total_eligible || 0);
            }
        } catch (err) {
            console.error("Failed to load collections:", err);
        } finally {
            setLoadingCollections(false);
        }
    };

    const loadCollectionQuestions = async (collectionId: string) => {
        if (!user?.id) return;
        setLoadingQuestions(true);
        setPickerQuestions([]);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/telegram/eligible-questions?user_id=${user.id}&collection_id=${collectionId}&bank=${activeBank}`
            );
            if (res.ok) {
                const data = await res.json();
                setPickerQuestions(data.questions || []);
            }
        } catch (err) {
            console.error("Failed to load questions:", err);
        } finally {
            setLoadingQuestions(false);
        }
    };

    const handleSelectCollection = (colId: string) => {
        setSelectedCollectionId(colId);
        setPickerView("questions");
        setSelectedQuestionId("");
        setPickerSearch("");
        setPickerSpecialtyFilter("all");
        loadCollectionQuestions(colId);
    };

    const handlePickerBack = () => {
        setPickerView("collections");
        setSelectedCollectionId(null);
        setSelectedQuestionId("");
        setPickerSearch("");
        setPickerSpecialtyFilter("all");
    };

    useEffect(() => {
        if (mainTab === "qbank") {
            setPickerView("collections");
            setSelectedCollectionId(null);
            setSelectedQuestionId("");
            loadCollections();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainTab]);

    const getFilteredQuestions = (): EligibleQuestion[] => {
        return pickerQuestions.filter((q) => {
            if (pickerSpecialtyFilter !== "all" && q.specialty !== pickerSpecialtyFilter) return false;
            if (pickerSearch.trim()) {
                return q.question_text.toLowerCase().includes(pickerSearch.toLowerCase());
            }
            return true;
        });
    };

    const getSpecialtiesInCollection = (): string[] => {
        const set = new Set(pickerQuestions.map((q) => q.specialty).filter(Boolean));
        return Array.from(set).sort();
    };

    const getSelectedCollectionTitle = (): string => {
        return pickerCollections.find((c) => c.id === selectedCollectionId)?.title || "Questions";
    };

    const handleGenerateQuiz = async () => {
        if (!user?.id || !selectedQuestionId) return;
        setGenerating(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/generate-quiz`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    question_id: selectedQuestionId,
                }),
            });

            if (res.ok) {
                const data = await res.json();
                setSelectedContent(data);
                setShowPreviewModal(true);
                showToast("Quiz post generated!", "success");
                // Reset picker
                setSelectedQuestionId("");
                setMainTab("posts");
                loadContents();
            } else {
                const error = await res.json();
                showToast(error.error || "Failed to generate quiz post", "error");
            }
        } catch (err) {
            console.error("Failed to generate quiz:", err);
            showToast("Failed to generate quiz post", "error");
        } finally {
            setGenerating(false);
        }
    };

    // =============================================
    // SETUP TAB
    // =============================================

    const loadBotInfo = useCallback(async () => {
        if (!user?.id) return;
        setLoadingBot(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/bot?user_id=${user.id}`);
            if (res.ok) {
                const data = await res.json();
                setBotInfo(data.bot || null);
            }
        } catch (err) {
            console.error("Failed to load bot info:", err);
        } finally {
            setLoadingBot(false);
        }
    }, [apiUrl, user?.id]);

    useEffect(() => {
        if (mainTab === "setup") loadBotInfo();
    }, [mainTab, loadBotInfo]);

    const handleVerifyBot = async () => {
        if (!setupToken.trim() || !setupChannelId.trim()) return;
        setVerifying(true);
        setVerifyResult(null);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/bot/verify`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user?.id,
                    bot_token: setupToken,
                    channel_id: setupChannelId,
                }),
            });
            const data = await res.json();
            setVerifyResult(data);
        } catch (err) {
            setVerifyResult({ success: false, error: "Network error" });
        } finally {
            setVerifying(false);
        }
    };

    const handleSaveBot = async () => {
        if (!user?.id || !verifyResult?.success) return;
        setSavingBot(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/bot`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    bot_token: setupToken,
                    bot_username: verifyResult.bot_username,
                    bot_id: verifyResult.bot_id,
                    channel_id: setupChannelId,
                    channel_title: verifyResult.channel_title,
                }),
            });
            if (res.ok) {
                setSetupToken("");
                setSetupChannelId("");
                setVerifyResult(null);
                showToast("Bot connected successfully!", "success");
                loadBotInfo();
            } else {
                const error = await res.json();
                showToast(error.error || "Failed to save bot", "error");
            }
        } catch (err) {
            showToast("Failed to save bot", "error");
        } finally {
            setSavingBot(false);
        }
    };

    const handleDisconnectBot = async () => {
        if (!user?.id || !window.confirm("Disconnect this bot?")) return;
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/bot`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id }),
            });
            if (res.ok) {
                setBotInfo(null);
            }
        } catch (err) {
            console.error("Failed to disconnect bot:", err);
        }
    };

    const handleTestMessage = async () => {
        if (!user?.id) return;
        setTestingSend(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/bot/test-message`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id }),
            });
            const data = await res.json();
            if (data.success) {
                showToast("Test message sent successfully!", "success");
            } else {
                showToast(`Failed: ${data.error || "Unknown error"}`, "error");
            }
        } catch (err) {
            showToast("Failed to send test message", "error");
        } finally {
            setTestingSend(false);
        }
    };

    // =============================================
    // EDIT MESSAGE IN PREVIEW MODAL
    // =============================================

    const handleStartEdit = () => {
        if (!selectedContent) return;
        const payload = selectedContent.content_payload || {};
        const key = selectedContent.content_type === "poll" ? "question" : "message";
        setEditMessageDraft(payload[key] || "");
        setEditingMessage(true);
    };

    const handleSaveEdit = async () => {
        if (!user?.id || !selectedContent) return;
        setSavingEdit(true);
        try {
            const key = selectedContent.content_type === "poll" ? "question" : "message";
            const updatedPayload = { ...selectedContent.content_payload, [key]: editMessageDraft };
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram/${selectedContent.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id, content_payload: updatedPayload }),
            });
            if (res.ok) {
                const updated = await res.json();
                setSelectedContent(updated);
                setEditingMessage(false);
                showToast("Content updated!", "success");
                loadContents();
            } else {
                showToast("Failed to save edit", "error");
            }
        } catch (err) {
            showToast("Failed to save edit", "error");
        } finally {
            setSavingEdit(false);
        }
    };

    // =============================================
    // RENDER
    // =============================================

    return (
        <div className={styles.container}>
            {/* Toast notification */}
            {toast && (
                <div className={toast.type === "success" ? styles.successBanner : styles.errorBanner} style={{ margin: "0 0 1rem 0" }}>
                    {toast.message}
                </div>
            )}

            {/* Main Tabs */}
            <div className={styles.header}>
                <div>
                    <h1>Telegram Channel · {activeBank.toUpperCase()}</h1>
                    <select value={activeBank} onChange={(event) => {
                        setActiveBank(event.target.value as QBankCode);
                        setPage(1);
                        setPickerCollections([]);
                        setPickerQuestions([]);
                    }} aria-label="QBank">
                        <option value="sdle">SDLE</option>
                        <option value="smle">SMLE</option>
                    </select>
                </div>
                <button
                    className={styles.calendarButton}
                    onClick={() => navigate(`/admin/telegram/calendar?bank=${activeBank}`)}
                >
                    Calendar View
                </button>
            </div>

            <div className={styles.mainTabs}>
                {(["posts", "compose", "qbank", "setup"] as const).map((tab) => (
                    <button
                        key={tab}
                        className={`${styles.mainTab} ${mainTab === tab ? styles.mainTabActive : ""}`}
                        onClick={() => setMainTab(tab)}
                    >
                        {tab === "posts" ? "Posts" : tab === "compose" ? "Compose" : tab === "qbank" ? "From QBank" : "Setup"}
                    </button>
                ))}
            </div>

            {/* ==================== POSTS TAB ==================== */}
            {mainTab === "posts" && (
                <div className={styles.tabContent}>
                    {/* Sub-filters */}
                    <div className={styles.tabs}>
                        {(["all", "ready", "scheduled", "posted"] as const).map((tab) => (
                            <button
                                key={tab}
                                className={`${styles.tab} ${activeFilter === tab ? styles.tabActive : ""}`}
                                onClick={() => {
                                    setActiveFilter(tab);
                                    setPage(1);
                                }}
                            >
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className={styles.loadingMessage}>Loading...</div>
                    ) : contents.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>No Telegram content yet.</p>
                            <p>Use the "Compose" or "From QBank" tab to create posts.</p>
                        </div>
                    ) : (
                        <>
                            <div className={styles.contentGrid}>
                                {contents.map((c) => (
                                    <div
                                        key={c.id}
                                        className={styles.contentCard}
                                        onClick={() => handleOpenPreview(c)}
                                    >
                                        <div className={styles.cardHeader}>
                                            <span className={styles.typeBadge}>
                                                {CONTENT_TYPE_ICONS[c.content_type] || c.content_type}
                                            </span>
                                            <span
                                                className={styles.statusBadge}
                                                style={{ backgroundColor: STATUS_LABELS[c.status]?.color || "#937663" }}
                                            >
                                                {STATUS_LABELS[c.status]?.label || c.status}
                                            </span>
                                        </div>
                                        <div className={styles.cardPreview}>
                                            {getContentPreview(c).slice(0, 100)}
                                            {getContentPreview(c).length > 100 && "..."}
                                        </div>
                                        {c.qbank_questions && (
                                            <div className={styles.cardSpecialty}>
                                                {c.qbank_questions.specialty}
                                            </div>
                                        )}
                                        <div className={styles.cardFooter}>
                                            {c.scheduling_status !== "unscheduled" && (
                                                <span
                                                    className={styles.schedulingBadge}
                                                    style={{
                                                        backgroundColor:
                                                            SCHEDULING_STATUS_LABELS[c.scheduling_status]?.color || "#937663",
                                                    }}
                                                >
                                                    {SCHEDULING_STATUS_LABELS[c.scheduling_status]?.label}
                                                </span>
                                            )}
                                            {c.scheduled_for && (
                                                <span className={styles.cardDate}>
                                                    {formatScheduledTime(c.scheduled_for)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className={styles.pagination}>
                                    <button
                                        className={styles.secondaryButton}
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => p - 1)}
                                    >
                                        Previous
                                    </button>
                                    <span className={styles.pageInfo}>
                                        Page {page} of {totalPages}
                                    </span>
                                    <button
                                        className={styles.secondaryButton}
                                        disabled={page >= totalPages}
                                        onClick={() => setPage((p) => p + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ==================== COMPOSE TAB ==================== */}
            {mainTab === "compose" && (
                <div className={styles.tabContent}>
                    <div className={styles.composeSection}>
                        {/* Type toggle */}
                        <div className={styles.composeTypeToggle}>
                            <button
                                className={`${styles.toggleButton} ${composeType === "text" ? styles.toggleActive : ""}`}
                                onClick={() => setComposeType("text")}
                            >
                                Text
                            </button>
                            <button
                                className={`${styles.toggleButton} ${composeType === "photo" ? styles.toggleActive : ""}`}
                                onClick={() => setComposeType("photo")}
                            >
                                Photo
                            </button>
                        </div>

                        {/* Message textarea */}
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                Message
                                <span className={styles.charCount}>
                                    {composeMessage.length}/{composeType === "photo" ? 1024 : 4096}
                                </span>
                            </label>
                            <textarea
                                className={styles.textarea}
                                value={composeMessage}
                                onChange={(e) => setComposeMessage(e.target.value)}
                                maxLength={composeType === "photo" ? 1024 : 4096}
                                rows={8}
                                placeholder="Enter your message..."
                            />
                        </div>

                        {/* Image URL for photo type */}
                        {composeType === "photo" && (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Image URL</label>
                                <input
                                    type="text"
                                    className={styles.textInput}
                                    value={composeImageUrl}
                                    onChange={(e) => setComposeImageUrl(e.target.value)}
                                    placeholder="https://example.com/image.jpg"
                                />
                            </div>
                        )}

                        {/* Parse mode */}
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Parse Mode</label>
                            <select
                                className={styles.select}
                                value={composeParseMode}
                                onChange={(e) => setComposeParseMode(e.target.value as "HTML" | "Markdown")}
                            >
                                <option value="HTML">HTML</option>
                                <option value="Markdown">Markdown</option>
                            </select>
                        </div>

                        {/* Preview area */}
                        {composeMessage.trim() && (
                            <div className={styles.composePreview}>
                                <h3 className={styles.previewTitle}>Preview</h3>
                                <div className={styles.previewBox}>
                                    {composeType === "photo" && composeImageUrl && (
                                        <div className={styles.previewImage}>
                                            <img src={composeImageUrl} alt="Preview" onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = "none";
                                            }} />
                                        </div>
                                    )}
                                    <div className={styles.previewText}>
                                        {composeMessage}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className={styles.composeActions}>
                            <button
                                className={styles.secondaryButton}
                                onClick={() => handleComposeSave(false)}
                                disabled={composeSaving || !composeMessage.trim()}
                            >
                                {composeSaving ? "Saving..." : "Save as Draft"}
                            </button>
                            <button
                                className={styles.primaryButton}
                                onClick={() => handleComposeSave(true)}
                                disabled={composeSaving || !composeMessage.trim()}
                            >
                                {composeSaving ? "Publishing..." : "Publish Now"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== QBANK TAB ==================== */}
            {mainTab === "qbank" && (
                <div className={styles.tabContent}>
                    <div className={styles.pickerContainer}>
                        {pickerView === "collections" ? (
                            <>
                                <div className={styles.pickerHeader}>
                                    <h2>Select a Collection</h2>
                                    <span className={styles.pickerCount}>
                                        {pickerTotalEligible} eligible question{pickerTotalEligible !== 1 ? "s" : ""}
                                    </span>
                                </div>

                                {loadingCollections ? (
                                    <div className={styles.loadingMessage}>Loading collections...</div>
                                ) : pickerCollections.length === 0 ? (
                                    <div className={styles.emptyState}>
                                        <p>No eligible questions found.</p>
                                    </div>
                                ) : (
                                    <div className={styles.collectionList}>
                                        {pickerCollections.map((col) => (
                                            <div
                                                key={col.id}
                                                className={styles.collectionItem}
                                                onClick={() => handleSelectCollection(col.id)}
                                            >
                                                <div className={styles.collectionTitle}>{col.title}</div>
                                                <div className={styles.collectionMeta}>
                                                    {col.eligible_count} eligible of {col.questions_count} total
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div className={styles.pickerHeader}>
                                    <button className={styles.backButton} onClick={handlePickerBack}>
                                        Back
                                    </button>
                                    <h2>{getSelectedCollectionTitle()}</h2>
                                </div>

                                {/* Search and filter */}
                                <div className={styles.pickerFilters}>
                                    <input
                                        type="text"
                                        className={styles.textInput}
                                        placeholder="Search questions..."
                                        value={pickerSearch}
                                        onChange={(e) => setPickerSearch(e.target.value)}
                                    />
                                    {getSpecialtiesInCollection().length > 1 && (
                                        <select
                                            className={styles.select}
                                            value={pickerSpecialtyFilter}
                                            onChange={(e) => setPickerSpecialtyFilter(e.target.value)}
                                        >
                                            <option value="all">All specialties</option>
                                            {getSpecialtiesInCollection().map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>

                                {loadingQuestions ? (
                                    <div className={styles.loadingMessage}>Loading questions...</div>
                                ) : (
                                    <div className={styles.questionList}>
                                        {getFilteredQuestions().map((q) => (
                                            <div
                                                key={q.id}
                                                className={`${styles.questionItem} ${selectedQuestionId === q.id ? styles.questionItemSelected : ""}`}
                                                onClick={() => setSelectedQuestionId(q.id)}
                                            >
                                                <div className={styles.questionText}>
                                                    {q.question_text}
                                                </div>
                                                <div className={styles.questionMeta}>
                                                    <span className={styles.specialtyTag}>{q.specialty}</span>
                                                    {q.difficulty && <span>{q.difficulty}</span>}
                                                </div>
                                                {selectedQuestionId === q.id && q.options && (
                                                    <div className={styles.pickerOptions}>
                                                        {q.options.map((opt) => (
                                                            <div
                                                                key={opt.label}
                                                                className={`${styles.pickerOption} ${opt.label === q.correct_answer ? styles.pickerOptionCorrect : ""}`}
                                                            >
                                                                <span className={styles.pickerOptionLabel}>{opt.label}.</span>
                                                                {opt.text}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Generate button */}
                                <div className={styles.pickerFooter}>
                                    <button
                                        className={styles.primaryButton}
                                        onClick={handleGenerateQuiz}
                                        disabled={!selectedQuestionId || generating}
                                    >
                                        {generating ? "Generating..." : "Generate Quiz Post"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ==================== SETUP TAB ==================== */}
            {mainTab === "setup" && (
                <div className={styles.tabContent}>
                    {loadingBot ? (
                        <div className={styles.loadingMessage}>Loading bot info...</div>
                    ) : botInfo ? (
                        /* Connected state */
                        <div className={styles.setupConnected}>
                            <div className={styles.botStatusCard}>
                                <h2>Connected Bot</h2>
                                <div className={styles.botDetail}>
                                    <span className={styles.detailLabel}>Bot</span>
                                    <span className={styles.detailValue}>@{botInfo.bot_username}</span>
                                </div>
                                <div className={styles.botDetail}>
                                    <span className={styles.detailLabel}>Channel</span>
                                    <span className={styles.detailValue}>
                                        {botInfo.channel_title || botInfo.channel_id}
                                    </span>
                                </div>
                                {botInfo.last_post_at && (
                                    <div className={styles.botDetail}>
                                        <span className={styles.detailLabel}>Last Post</span>
                                        <span className={styles.detailValue}>
                                            {formatScheduledTime(botInfo.last_post_at)}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className={styles.setupActions}>
                                <button
                                    className={styles.secondaryButton}
                                    onClick={handleTestMessage}
                                    disabled={testingSend}
                                >
                                    {testingSend ? "Sending..." : "Send Test Message"}
                                </button>
                                <button className={styles.dangerButton} onClick={handleDisconnectBot}>
                                    Disconnect
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Disconnected state - setup wizard */
                        <div className={styles.setupWizard}>
                            <h2>Connect a Telegram Bot</h2>
                            <div className={styles.setupInstructions}>
                                <ol>
                                    <li>Open Telegram and message <strong>@BotFather</strong></li>
                                    <li>Send <code>/newbot</code> and follow the prompts</li>
                                    <li>Copy the bot token provided</li>
                                    <li>Add your bot as an admin to your channel</li>
                                    <li>Enter the bot token and channel ID below</li>
                                </ol>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Bot Token</label>
                                <input
                                    type="password"
                                    className={styles.textInput}
                                    value={setupToken}
                                    onChange={(e) => {
                                        setSetupToken(e.target.value);
                                        setVerifyResult(null);
                                    }}
                                    placeholder="123456789:ABCDefGh..."
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Channel ID</label>
                                <input
                                    type="text"
                                    className={styles.textInput}
                                    value={setupChannelId}
                                    onChange={(e) => {
                                        setSetupChannelId(e.target.value);
                                        setVerifyResult(null);
                                    }}
                                    placeholder="@my_channel or -1001234567890"
                                />
                            </div>

                            {verifyResult && (
                                <div
                                    className={
                                        verifyResult.success ? styles.successBanner : styles.errorBanner
                                    }
                                >
                                    {verifyResult.success ? (
                                        <>
                                            Bot: <strong>@{verifyResult.bot_username}</strong> |
                                            Channel: <strong>{verifyResult.channel_title}</strong>
                                        </>
                                    ) : (
                                        verifyResult.error
                                    )}
                                </div>
                            )}

                            <div className={styles.setupActions}>
                                <button
                                    className={styles.secondaryButton}
                                    onClick={handleVerifyBot}
                                    disabled={verifying || !setupToken.trim() || !setupChannelId.trim()}
                                >
                                    {verifying ? "Verifying..." : "Verify Connection"}
                                </button>
                                {verifyResult?.success && (
                                    <button
                                        className={styles.primaryButton}
                                        onClick={handleSaveBot}
                                        disabled={savingBot}
                                    >
                                        {savingBot ? "Saving..." : "Save & Connect"}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ==================== PREVIEW MODAL ==================== */}
            {showPreviewModal && selectedContent && (
                <div className={styles.modalOverlay} onClick={() => { setShowPreviewModal(false); setEditingMessage(false); }}>
                    <div
                        className={`${styles.modal} ${styles.previewModal}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            <h2>Content Preview</h2>
                            <button
                                className={styles.closeButton}
                                onClick={() => { setShowPreviewModal(false); setEditingMessage(false); }}
                            >
                                &times;
                            </button>
                        </div>

                        <div className={styles.previewContent}>
                            {/* Type indicator */}
                            <div className={styles.previewTypeRow}>
                                <span className={styles.typeBadge}>
                                    {CONTENT_TYPE_ICONS[selectedContent.content_type] || selectedContent.content_type}
                                </span>
                                <span
                                    className={styles.statusBadge}
                                    style={{ backgroundColor: STATUS_LABELS[selectedContent.status]?.color }}
                                >
                                    {STATUS_LABELS[selectedContent.status]?.label}
                                </span>
                                {selectedContent.status === "ready" && !editingMessage && (
                                    <button className={styles.editButton} onClick={handleStartEdit}>
                                        Edit
                                    </button>
                                )}
                            </div>

                            {/* Editing mode */}
                            {editingMessage ? (
                                <div className={styles.editSection}>
                                    <textarea
                                        className={styles.textarea}
                                        value={editMessageDraft}
                                        onChange={(e) => setEditMessageDraft(e.target.value)}
                                        rows={6}
                                    />
                                    <div className={styles.editActions}>
                                        <button
                                            className={styles.secondaryButton}
                                            onClick={() => setEditingMessage(false)}
                                            disabled={savingEdit}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className={styles.primaryButton}
                                            onClick={handleSaveEdit}
                                            disabled={savingEdit}
                                        >
                                            {savingEdit ? "Saving..." : "Save"}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Content preview by type */}
                                    {selectedContent.content_type === "poll" ? (
                                        <div className={styles.pollPreview}>
                                            <div className={styles.pollQuestion}>
                                                {selectedContent.content_payload?.question}
                                            </div>
                                            <div className={styles.pollOptions}>
                                                {(selectedContent.content_payload?.options || []).map(
                                                    (opt: string, idx: number) => (
                                                        <div
                                                            key={idx}
                                                            className={`${styles.pollOption} ${idx === selectedContent.content_payload?.correct_option_id ? styles.pollOptionCorrect : ""}`}
                                                        >
                                                            {opt}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                            {selectedContent.content_payload?.explanation && (
                                                <div className={styles.pollExplanation}>
                                                    <strong>Explanation:</strong>{" "}
                                                    {selectedContent.content_payload.explanation}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className={styles.textPreviewSection}>
                                            {selectedContent.content_type === "photo" &&
                                                selectedContent.content_payload?.image_url && (
                                                    <div className={styles.previewImage}>
                                                        <img
                                                            src={selectedContent.content_payload.image_url}
                                                            alt="Preview"
                                                            onError={(e) => {
                                                                (e.target as HTMLImageElement).style.display = "none";
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            <div className={styles.previewText}>
                                                {selectedContent.content_payload?.message || ""}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Error display */}
                        {selectedContent.scheduling_status === "failed" &&
                            selectedContent.last_publish_error && (
                                <div className={styles.errorBanner}>
                                    <strong>Publishing failed:</strong>{" "}
                                    {selectedContent.last_publish_error}
                                </div>
                            )}

                        {/* Posted info */}
                        {selectedContent.status === "posted" && selectedContent.posted_at && (
                            <div className={styles.schedulingInfo}>
                                Posted on: {formatScheduledTime(selectedContent.posted_at)}
                            </div>
                        )}

                        {/* Scheduling info */}
                        {selectedContent.scheduling_status === "scheduled" &&
                            selectedContent.scheduled_for && (
                                <div className={styles.schedulingInfo}>
                                    Scheduled for: {formatScheduledTime(selectedContent.scheduled_for)}
                                </div>
                            )}

                        <div className={styles.modalFooter}>
                            {selectedContent.status !== "posted" && (
                                <button
                                    className={styles.dangerButton}
                                    onClick={() => handleDelete(selectedContent.id)}
                                >
                                    Delete
                                </button>
                            )}
                            {selectedContent.status === "ready" && (
                                <>
                                    {selectedContent.scheduling_status === "failed" ? (
                                        <>
                                            <button
                                                className={styles.secondaryButton}
                                                onClick={() =>
                                                    handleOpenScheduleModal(selectedContent.id, null)
                                                }
                                            >
                                                Schedule
                                            </button>
                                            <button
                                                className={styles.primaryButton}
                                                onClick={() => handleRetry(selectedContent.id)}
                                                disabled={publishing}
                                            >
                                                {publishing ? "Posting..." : "Retry Post"}
                                            </button>
                                        </>
                                    ) : selectedContent.scheduling_status === "scheduled" ? (
                                        <>
                                            <button
                                                className={styles.secondaryButton}
                                                onClick={() => handleUnschedule(selectedContent.id)}
                                            >
                                                Unschedule
                                            </button>
                                            <button
                                                className={styles.secondaryButton}
                                                onClick={() => handleMarkPosted(selectedContent.id)}
                                            >
                                                Mark as Posted
                                            </button>
                                            <button
                                                className={styles.primaryButton}
                                                onClick={() => handlePublish(selectedContent.id)}
                                                disabled={publishing}
                                            >
                                                {publishing ? "Posting..." : "Post Now"}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                className={styles.secondaryButton}
                                                onClick={() =>
                                                    handleOpenScheduleModal(selectedContent.id, null)
                                                }
                                            >
                                                Schedule
                                            </button>
                                            <button
                                                className={styles.primaryButton}
                                                onClick={() => handlePublish(selectedContent.id)}
                                                disabled={publishing}
                                            >
                                                {publishing ? "Posting..." : "Post Now"}
                                            </button>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Schedule Modal */}
            {showScheduleModal && schedulingContentId && (
                <ScheduleModal
                    onSchedule={handleSchedule}
                    onClose={() => {
                        setShowScheduleModal(false);
                        setSchedulingContentId(null);
                        setSchedulingInitialDate(null);
                    }}
                    initialDate={schedulingInitialDate}
                />
            )}
        </div>
    );
};

export default AdminTelegram;
